// unit-menu-view.ts — the in-game GOB CONTEXT MENU (UnitMenu) DOM overlay (HTML/DOM
// UI shell plan, Phase 5, build/order/recruit/place STEP 2). Renders the canvas
// UnitMenu (game/Unit.cpp:1079-1350) — a small floating menu anchored OVER the tapped
// gob's screen rect: a title (the gob name in the side color) + the per-gob-type
// bitmap buttons (Build/AbortBuild · Repair/AbortRepair · Sell · Research/AbortUpgrade
// · Transform/Deliver), exactly the buttons the canvas shows for that gob, LIVE.
//
// COMMAND-ADJACENT, DISPLAY-ONLY: this view builds NO command and constructs NO
// Message. It re-polls a pure read-model (BuildUI.GetMenuModel via the host's
// getModel) on the canvas's ~25ms cadence so the visible/enabled buttons track the gob
// state (e.g. Build↔AbortBuild as the queue changes) AND the anchor follows the gob as
// the map scrolls. Tapping a button reports its idc through onSelect, which the host
// routes to the SAME OnMenuItemSelected the canvas continuation uses (the command stays
// in buildui.ts). AutoTakedown: tapping the click-catcher backdrop closes it (onClose);
// getModel returning null (the gob deactivated/deselected) also closes it.
//
// FAITHFUL CHROME (UnitMenu.SetOwner buildui.ts:497-661 + OnPaintBackground :664-683):
// the menu is NOT a vertical column — the canvas packs the visible buttons into a single
// HORIZONTAL BUTTON BAR (so the HQ menu is a 3-wide Build/Repair/Sell icon row) and puts
// the gob-name TITLE in a translucent ShadowHelper strip ABOVE the gob, the button bar
// BELOW it, the gob's UIBounds rect between them, all centred on the gob and kept on
// screen. BOTH boxes get a 1px black border — EXCEPT a single-button menu (MobileHQ
// Transform / Miner Deliver) gets no button-bar border (cButtons===1 → m_rcButtons
// .SetEmpty). The side colour is the TITLE TEXT colour, NOT a bar fill (the strip is the
// translucent shadow). The buttons are BITMAP buttons (build_btn/repair_btn/abort/sell
// up/down/disabled PNGs, recolored kside1) — the bitmaps carry the icon AND label — drawn
// at the canvas magnification (game px × gameScale → CSS px, matching the canvas which
// presents its game-px back buffer scaled by kGameScale). The Transform/Deliver TEXT
// buttons (no PNG) render the SAME 9-slice frame the shell/confirm buttons use + a web
// label (the canvas draws them as a standard ButtonControl frame).
//
// The TITLE text is WHITE (the canvas kidcTitle LabelControl sets no text colour, so the
// kifntTitle font draws its default white — NOT the gob's side colour; SetOwner does no
// SetTextColor) and rendered AS-IS — the gob name (szName) is NOT uppercased (Unit.cpp:1117).
// kifntTitle (SteelWolf) is caps-only but its lowercase code points are subtly different
// glyphs, so we preserve the name's case to match the canvas. Only the confirm DialogForm's
// title BAR is side-coloured (a fill), not this.

// NOTE (Phase 2 chrome de-dup): the UnitMenu is NOT a design-stage form — it is
// anchor-positioned over the gob at the canvas magnification, NOT the chrome.ts
// formScale family, and its buttons are bitmap up/down/disabled state machines (the
// text Transform/Deliver path is a one-off 9-slice + an enabled/disabled colour map,
// wired through wireButton, no cache). None of the shared chrome.ts primitives
// (formScale/makeStage/at/the cached makeStageButton) fit without changing this view's
// output, so this view keeps its own — by design (correctness over dedup).
import { ksfxGuiButtonTap } from '../../ui/uikit.ts';
import { type ButtonArt, composeButtonDataUrl } from '../title-art.ts';
import {
  SHADOW_BG,
  COL_BORDER_BLACK,
  el,
  FONT_TITLE,
  FONT_BUTTON,
  FONT_BODY,
  buttonWidthU,
  rootBox,
} from '../chrome.ts';
import type { UnitMenuModel, UnitMenuButton, UnitMenuButtonArt } from '../../ui/buildui.ts';

// the WI faces (out/fontid): the gob-name TITLE = Quadtrangle (kifntTitle), the
// Transform/Deliver button labels = Eurostile (kifntButton); FONT = the body face.
const FONT = FONT_BODY;

export interface UnitMenuViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the CURRENT read-model, or null when the menu should close (the gob is gone). The
   *  view polls this on the refresh cadence — buttons + anchor track the live gob. */
  getModel: () => UnitMenuModel | null;
  /** a button was tapped → the host routes idc to OnMenuItemSelected (the command path
   *  stays in buildui.ts). The view constructs no command. */
  onSelect: (idc: number) => void;
  /** AutoTakedown / gob-gone → the host unmounts the overlay. */
  onClose: () => void;
  /** the CHROME magnification (kUiMagnify): the menu's bitmap buttons + title are drawn at
   *  game px × this. Decoupled from the map scale so the menu's TAP TARGETS grow on phones.
   *  The menu's PLACEMENT over the gob uses {@link anchorMagnify}, not this. */
  magnify: number;
  /** the MAP present scale (kGameScale): game px → CSS px for the gob's on-screen rect, so
   *  the menu stays anchored OVER the gob (placement must match the canvas, which presents
   *  the playfield at this factor). Was folded into `magnify`; split out so the chrome can
   *  grow on phones without the menu drifting off its target. Defaults to `magnify`. */
  anchorMagnify?: number;
  /** the 9-slice button frame (the shell/confirm pack art) for the TEXT buttons
   *  (Transform / Deliver, which have no per-button PNG) — they render this frame + a
   *  web label, like the canvas standard ButtonControl. Absent ⇒ a flat CSS fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

// the canvas re-runs InitMenu on a 25ms timer (Unit.cpp:1087-1101) — match it so the
// buttons + anchor refresh live.
const kRefreshMs = 25;

// SetOwner layout constants (buildui.ts:459-461,133), in GAME px — reproduced so the
// DOM button-bar / title-strip lays out exactly like the canvas UnitMenu:
//   kcxButtonSpace=0 (no gap between the packed buttons in the horizontal bar);
//   kcyTitleMargin=3 / kcxTitleMargin=5 (the title text is inflated by these to form the
//   shadow strip, then +1 for the black border — buildui.ts:671,676);
//   kcyHealthBar=4 (the vertical gap above/below the gob's UIBounds rect).
const kcxButtonSpace = 0;
const kcyTitleMargin = 3;
const kcxTitleMargin = 5;
const kcyHealthBar = 4;

// the ShadowHelper translucency (kiclrShadow) — the playfield/gob shows through, darkened.
// SAME value the confirm DialogForm body uses (confirm-view.ts:158).
const kShadowBg = SHADOW_BG;
// the 1px-black-border colour (DrawBorder kiclrBlack, buildui.ts:677,682).
const kBorder = COL_BORDER_BLACK;

export class UnitMenuView {
  private readonly d: UnitMenuViewDeps;
  private timer: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  /** the menu panel (positioned absolutely over the canvas; rebuilt when the model's
   *  button set changes — not every poll, to avoid churning the DOM). The panel is the
   *  un-bordered, un-painted container; the canvas SetOwner paints TWO separate boxes
   *  inside it — the title strip ABOVE the gob and the button bar BELOW it
   *  (buildui.ts:567-661,664-683) — which the view keeps as child elements to position
   *  + clamp independently. */
  private panel: HTMLElement | null = null;
  /** the title strip (ShadowHelper + 1px black border) — positioned above the gob. */
  private titleEl: HTMLElement | null = null;
  /** the horizontal button bar (1px black border unless single-button) — below the gob. */
  private barEl: HTMLElement | null = null;
  /** the "Can't Transform…" label (MobileHQ) — sits where the button bar would. */
  private cantEl: HTMLElement | null = null;
  /** a fingerprint of the last-rendered button set + cantTransform, so we only rebuild
   *  the buttons when they actually change (Build↔AbortBuild), not every 25ms tick. */
  private lastButtonsKey = '';
  /** the bitmap data-URL cache (keyed by idc+state) — the bitmaps are stable per gob. */
  private readonly artCache = new Map<string, string>();

  constructor(d: UnitMenuViewDeps) {
    this.d = d;
    const s = d.root.style;
    // a fixed, full-window overlay above the HUD (z=20, the in-game modal layer). The
    // CONTAINER is a click-catcher (AutoTakedown): a pointerdown anywhere that is NOT a
    // menu button closes the menu — matching the canvas Form.OnHitTest AutoTakedown arm
    // (form.ts:737-745). The buttons re-enable pointer-events on themselves.
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '20';
    s.color = '#e8eef0';
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'auto'; // the backdrop catches taps → close (AutoTakedown)
    s.background = 'transparent';
    s.overflow = 'hidden';
    d.root.addEventListener('pointerdown', this.onBackdropDown);
    window.addEventListener('keydown', this.onKeyDown);
    this.render(); // first paint
    this.timer = setInterval(this.tick, kRefreshMs);
  }

  // a pointerdown on the backdrop (NOT a button — buttons stopPropagation) → close.
  private readonly onBackdropDown = (e: PointerEvent): void => {
    if (e.target === this.d.root) {
      e.preventDefault();
      this.close();
    }
  };

  // ANY key dismisses the menu — the canvas Form.OnKeyTest fires EndForm(kidcCancel) on
  // any key event while AutoTakedown (form.ts:757-760), not just Escape. (Ignore lone
  // modifier presses so a chord's modifier doesn't pre-close it.)
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') return;
    e.preventDefault();
    this.close();
  };

  destroy(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.d.root.removeEventListener('pointerdown', this.onBackdropDown);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  /** close ONCE (the host unmounts; getModel may already have gone null). */
  private close(): void {
    if (this.closed) return;
    this.closed = true;
    this.d.onClose();
  }

  // the refresh cadence: re-poll the model; null → close; else re-render (cheap unless
  // the button set changed).
  private readonly tick = (): void => {
    if (this.closed) return;
    const model = this.d.getModel();
    if (model === null) {
      this.close();
      return;
    }
    this.render(model);
  };

  private render(model?: UnitMenuModel | null): void {
    const m = model === undefined ? this.d.getModel() : model;
    if (m === null) {
      this.close();
      return;
    }

    const mag = this.d.magnify;
    // a fingerprint that captures the button SET + enable + the cantTransform label —
    // rebuild the panel contents only when these change (queue/upgrade toggles).
    const key =
      m.buttons.map((b) => `${b.idc}:${b.enabled ? 1 : 0}`).join(',') +
      '|' +
      (m.cantTransform ?? '') +
      '|' +
      m.title;
    if (this.panel === null || key !== this.lastButtonsKey) {
      this.buildPanel(m, mag);
      this.lastButtonsKey = key;
      this.d.root.replaceChildren(this.panel!);
    }
    // ALWAYS reposition (the anchor follows the gob as the map scrolls). Placement uses the
    // MAP scale (anchorMagnify = kGameScale), NOT the chrome `mag`, so the menu tracks the
    // gob even when the chrome is magnified larger than the map on phones.
    this.position(m, this.d.anchorMagnify ?? mag);
  }

  // build the menu chrome — FAITHFUL to UnitMenu.SetOwner (buildui.ts:497-661) +
  // OnPaintBackground (buildui.ts:664-683): NOT a vertical column. The canvas packs the
  // visible buttons into a single HORIZONTAL BUTTON BAR (the HQ menu is thus a 3-wide
  // icon row), draws the gob-name title in a translucent ShadowHelper strip ABOVE the
  // gob, and frames BOTH with a 1px black border — except a single-button menu
  // (Transform/Deliver) gets NO button-bar border (cButtons===1 → m_rcButtons.SetEmpty,
  // buildui.ts:656). The title strip + button bar are TWO separate boxes (title above
  // the gob, buttons below) positioned/clamped independently in position().
  private buildPanel(m: UnitMenuModel, mag: number): void {
    // a transparent, un-bordered container holding the two painted boxes; it never
    // catches taps itself (the buttons re-enable pointer-events on themselves).
    const panel = el('div', { position: 'absolute', pointerEvents: 'none' });
    panel.dataset.unitMenuPanel = '1';

    // ── the TITLE strip — the gob name in WHITE, rendered AS-IS (the canvas kidcTitle =
    //    m_punt->GetConsts()->szName, NOT uppercased — game/Unit.cpp:1117; m.titleColor is
    //    white). kifntTitle (SteelWolf) is a caps-only display face, BUT its lowercase code
    //    points are subtly DIFFERENT glyphs (still cap-height), so we must preserve the gob
    //    name's case (NO textTransform) — forcing uppercase swaps "Eagle"/"Dominion" to the
    //    plain-uppercase glyphs and loses the lowercase variants the canvas shows. (The build
    //    panel NAME is separately HtStrupr'd, so it IS all-uppercase — that's faithful.) Over a
    //    translucent ShadowHelper strip with a 1px black border (B-G2, buildui.ts:664-677). The
    //    text is inflated by (kcxTitleMargin, kcyTitleMargin) game px → the strip padding.
    const title = el('div', {
      position: 'absolute',
      display: 'inline-block',
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      padding: `${Math.round(kcyTitleMargin * mag)}px ${Math.round(kcxTitleMargin * mag)}px`,
      background: kShadowBg,
      border: `1px solid ${kBorder}`,
      // the gob-name TITLE = the canvas kifntTitle (GetHeight 13 game px). MEASURED against
      // the canvas render: the rendered "DOMINION" glyph is 15px CSS at gameScale 1.5, so fontSize
      // 15*mag gives the matching glyph (SteelWolf cap-height ≈ 0.67·em); 10*mag was ~⅓ too
      // small (and made the button look too big by comparison). The translucent strip grows
      // with the font (padding unchanged), as the canvas SetOwner inflate does. +0.05em
      // tracking (~1px) like the other titles.
      fontFamily: FONT_TITLE,
      fontSize: `${Math.round(15 * mag)}px`,
      fontWeight: '400',
      letterSpacing: '0.05em',
      color: m.titleColor,
      textAlign: 'center',
      // line-height < 1 so the strip hugs the glyph like the canvas (its title cell is
      // GetHeight=13 game px ≈ 19.5px, not the 22.5px em a 15*mag line-box gives) → the box
      // reads ~28px tall like the canvas instead of ~35.
      textShadow: '0 1px 2px rgba(0,0,0,0.9)',
      lineHeight: '0.75',
      pointerEvents: 'none',
    });
    title.textContent = m.title;
    title.dataset.unitMenuTitle = '1';
    panel.append(title);
    this.titleEl = title;

    // ── the BUTTON BAR — a single HORIZONTAL row of the visible bitmap buttons
    //    (buildui.ts:639-651: each button advances by cxButton + kcxButtonSpace, no
    //    wrap). 1px black border (buildui.ts:679-682) UNLESS there is exactly one button
    //    (B-G3 / buildui.ts:656: Transform/Deliver get no surrounding border).
    this.barEl = null;
    this.cantEl = null;
    if (m.buttons.length > 0) {
      const single = m.buttons.length === 1;
      const bar = el('div', {
        position: 'absolute',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: `${Math.round(kcxButtonSpace * mag)}px`,
        background: single ? 'transparent' : kShadowBg,
        border: single ? 'none' : `1px solid ${kBorder}`,
        boxSizing: 'border-box',
        pointerEvents: 'none',
      });
      bar.dataset.unitMenuBar = single ? 'single' : '1';
      for (const b of m.buttons) bar.append(this.button(b, mag));
      panel.append(bar);
      this.barEl = bar;
    }

    // kidfMobileHqMenu's "Can't Transform…" label (shown when Transform is hidden) —
    // a translucent strip where the button bar would sit (no button-bar border).
    if (m.cantTransform !== null && m.cantTransform !== '') {
      const lbl = el('div', {
        position: 'absolute',
        boxSizing: 'border-box',
        padding: `${Math.round(2 * mag)}px ${Math.round(3 * mag)}px`,
        background: kShadowBg,
        border: `1px solid ${kBorder}`,
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * mag)}px`,
        color: '#cdd6da',
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
        maxWidth: `${Math.round(62 * mag)}px`,
        lineHeight: '1.2',
        pointerEvents: 'none',
      });
      lbl.textContent = m.cantTransform;
      lbl.dataset.unitMenuCantTransform = '1';
      panel.append(lbl);
      this.cantEl = lbl;
    }
    this.panel = panel;
  }

  // a single menu button. FAITHFUL: a bitmap button (up/down/disabled PNGs, the icon +
  // label baked in) drawn at the canvas magnification; the Transform/Deliver TEXT
  // buttons (no bitmap) fall back to a web-font button.
  private button(b: UnitMenuButton, mag: number): HTMLButtonElement {
    const tap = (): void => {
      this.d.playSfx?.(ksfxGuiButtonTap);
    };
    const hasArt = b.art.up.cx > 0 && b.art.up.cy > 0;

    if (!hasArt) {
      // the Transform/Deliver TEXT button — the canvas draws a standard ButtonControl
      // frame + the kifntButton label, so render the SAME 9-slice frame (composited) + a
      // web label (matching the shell/confirm buttons), NOT a flat pill. HEIGHT: the canvas
      // ButtonControl IGNORES the forms.ini height (3,14,60,12) and forces it to the standard
      // button mid-bitmap's native cy (misccontrols.ts:308-313) — the same height as the
      // bitmap icon buttons — so use midUp.cy * mag here too (12u was squashed). WIDTH: sized
      // to the label via buttonWidthU so the wide WI face keeps clear margin in the frame.
      const art = this.d.buttonArt;
      const hU = art?.midUp.cy ?? 12;
      const wDesign = buttonWidthU(b.label, hU, mag);
      const w = Math.round(wDesign * mag),
        h = Math.round(hU * mag);
      const btn = el('button', {
        width: `${w}px`,
        height: `${h}px`,
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.max(8, Math.round(h * 0.5))}px`,
        fontWeight: '600',
        fontFamily: FONT_BUTTON,
        whiteSpace: 'nowrap',
        cursor: b.enabled ? 'pointer' : 'default',
        pointerEvents: 'auto',
        color:
          art !== undefined ? (b.enabled ? '#fff' : '#7a8a8e') : b.enabled ? '#0b1316' : '#5a6a6e',
        textShadow: art !== undefined ? '0 1px 2px rgba(0,0,0,0.7)' : 'none',
        border: art !== undefined ? 'none' : `1px solid ${kBorder}`,
        background: art !== undefined ? 'transparent' : b.enabled ? '#8fd0e0' : '#324044',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '100% 100%',
        imageRendering: 'pixelated',
        opacity: b.enabled ? '1' : '0.6',
      }) as HTMLButtonElement;
      if (art !== undefined) {
        const wArt = Math.max(1, Math.round((w / h) * art.midUp.cy));
        btn.style.backgroundImage = `url(${composeButtonDataUrl(art, wArt, false)})`;
      }
      btn.textContent = b.label;
      btn.dataset.unitMenuBtn = String(b.idc);
      btn.dataset.unitMenuEnabled = b.enabled ? '1' : '0';
      this.wireButton(btn, b, tap);
      return btn;
    }

    // the bitmap button: size to the art's NATIVE px × the canvas magnification (game
    // px → CSS px, like the canvas blits at native size scaled by kGameScale). The
    // up/down/disabled state swaps the background image.
    const w = Math.round(b.art.up.cx * mag),
      h = Math.round(b.art.up.cy * mag);
    const btn = el('button', {
      width: `${w}px`,
      height: `${h}px`,
      padding: '0',
      border: 'none',
      background: 'transparent',
      cursor: b.enabled ? 'pointer' : 'default',
      pointerEvents: 'auto',
      imageRendering: 'pixelated',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
    }) as HTMLButtonElement;
    btn.dataset.unitMenuBtn = String(b.idc);
    btn.dataset.unitMenuEnabled = b.enabled ? '1' : '0';
    btn.dataset.unitMenuArt = '1';
    btn.setAttribute('aria-label', b.label);

    const setArt = (state: 'up' | 'down' | 'disabled'): void => {
      btn.style.backgroundImage = `url(${this.artUrl(b.idc, b.art, state)})`;
    };
    setArt(b.enabled ? 'up' : 'disabled');
    if (b.enabled) {
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        setArt('down');
        tap();
      });
      const rel = (): void => setArt('up');
      btn.addEventListener('pointerup', rel);
      btn.addEventListener('pointerleave', rel);
      btn.addEventListener('pointercancel', rel);
    } else {
      // disabled buttons still swallow the tap (so it doesn't fall through to the
      // backdrop's AutoTakedown close), but route nothing — like the canvas.
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
    }
    this.wireButton(btn, b, tap);
    return btn;
  }

  private wireButton(btn: HTMLButtonElement, b: UnitMenuButton, tap: () => void): void {
    // stop the pointerdown from reaching the backdrop (so a button tap never doubles as
    // a takedown), and route a click → onSelect (only when enabled).
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (btn.dataset.unitMenuEnabled !== '1') return;
      if (b.art.up.cx === 0) tap(); // bitmap path already tapped on pointerdown
      // report the selection; the host unmounts FIRST then routes to OnMenuItemSelected
      // (Build opens the build panel, Sell/Abort → domConfirm, others enqueue).
      this.close();
      this.d.onSelect(b.idc);
    });
  }

  // position the TITLE strip above the gob + the BUTTON BAR below it (UnitMenu.SetOwner,
  // buildui.ts:567-651): the gob's UIBounds rect sits between them. Each box is centred
  // horizontally on the gob and the whole menu is kept on screen by shifting it so a
  // partly-off-screen gob keeps its menu adjacent (the canvas dx/dy keep-on-screen,
  // buildui.ts:588-596). game px × the MAP scale (anchorMagnify) → CSS px, so the menu sits
  // over the gob on the canvas; the chrome box sizes come from offsetWidth (already × the
  // chrome magnify), so a larger chrome simply centres over the same gob anchor.
  private position(m: UnitMenuModel, mag: number): void {
    const panel = this.panel;
    if (panel === null) return;
    // the gob's on-screen rect in CSS px (the SetOwner rcUIBounds pipeline). Coordinates
    // inside the root ARE playfield coordinates: the root spans exactly the playfield box
    // (the window in the shipped client; the play pane in the mission editor, whose host
    // element bounds the fixed-position root — see chrome.rootBox).
    const vp = rootBox(this.d.root);
    const ax = m.anchor.x * mag,
      ay = m.anchor.y * mag;
    const aw = m.anchor.w * mag,
      ah = m.anchor.h * mag;
    const gobCx = ax + aw / 2;
    const gap = Math.round(kcyHealthBar * mag); // the kcyHealthBar gap above/below the gob

    const title = this.titleEl;
    const bar = this.barEl ?? this.cantEl; // the cant-transform label sits where the bar would
    const tw = title?.offsetWidth ?? 0,
      th = title?.offsetHeight ?? 0;
    const bw = bar?.offsetWidth ?? 0,
      bh = bar?.offsetHeight ?? 0;

    // SetOwner stacks: title (top) — gap — gob UIBounds — gap — buttons (bottom). The
    // TITLE sits above the hilight SPRITE top (anchor.titleTop), not the UIBounds top
    // (buildui.ts:571-578); the BUTTONS sit below the UIBounds bottom (ay + ah).
    const titleTopPx = m.anchor.titleTop * mag;
    let stackTop = titleTopPx - gap - th;
    let stackBottom = ay + ah + gap + bh;
    // the menu's bounding box (max of the two box widths, centred on the gob).
    const menuW = Math.max(tw, bw);
    let menuLeft = gobCx - menuW / 2;

    // keep-on-screen: shift dx/dy so the menu stays in the PLAYFIELD box (buildui.ts:588-596
    // — the canvas clamps to its own back buffer, which IS the playfield box).
    let dx = 0,
      dy = 0;
    if (menuLeft < 2) dx = 2 - menuLeft;
    else if (menuLeft + menuW > vp.width - 2) dx = vp.width - 2 - menuW - menuLeft;
    if (stackTop < 2) dy = 2 - stackTop;
    else if (stackBottom > vp.height - 2) dy = vp.height - 2 - stackBottom;
    menuLeft += dx;
    stackTop += dy;
    stackBottom += dy;

    // the PANEL container spans the whole menu box; the boxes are placed relative to it.
    panel.style.left = `${Math.round(menuLeft)}px`;
    panel.style.top = `${Math.round(stackTop)}px`;
    panel.style.width = `${Math.round(menuW)}px`;
    panel.style.height = `${Math.round(stackBottom - stackTop)}px`;

    // each box centred within the menu box (relative coords), title at the top, bar at
    // the bottom (matching the canvas: title above the gob, buttons below).
    if (title !== null) {
      title.style.left = `${Math.round((menuW - tw) / 2)}px`;
      title.style.top = '0px';
    }
    if (bar !== null) {
      bar.style.left = `${Math.round((menuW - bw) / 2)}px`;
      bar.style.top = `${Math.round(stackBottom - stackTop - bh)}px`;
    }
  }

  private artUrl(idc: number, art: UnitMenuButtonArt, state: 'up' | 'down' | 'disabled'): string {
    const key = `${idc}:${state}`;
    let u = this.artCache.get(key);
    if (u === undefined) {
      const src =
        state === 'down' ? art.down : state === 'disabled' ? (art.disabled ?? art.up) : art.up;
      u = rgbaToDataUrl(src);
      this.artCache.set(key, u);
    }
    return u;
  }
}

// encode an RGBA buffer (ImageData byte order — the CPU present path's buffer) as a PNG
// data URL (the same encoding title-art.ts uses).
function rgbaToDataUrl(art: { cx: number; cy: number; rgba: Uint8Array }): string {
  const canvas = document.createElement('canvas');
  canvas.width = art.cx;
  canvas.height = art.cy;
  const ctx = canvas.getContext('2d')!;
  const buf = new Uint8ClampedArray(art.rgba.length);
  buf.set(art.rgba);
  ctx.putImageData(new ImageData(buf, art.cx, art.cy), 0, 0);
  return canvas.toDataURL('image/png');
}
